// src/pages/Leite/propriedade/Assistente.jsx
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    Paper,
    Skeleton,
    Stack,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import Mascote from '../../../assets/mascote/mascote-bioailab.svg';
import MascoteEscuro from '../../../assets/mascote/mascote-bioailab-escuro.svg';
import { AZUL_CLARO } from '../../../theme/cores';
import { iconeDaAcao, rotaDaAcao } from './acoesSugestao';

/**
 * Mascote do assistente: o bacilo cientista. No tema escuro os flagelos e o elástico dos
 * óculos ficam azul-claros, senão somem no fundo.
 */
export function AvatarAssistente({ tamanho = 64 }) {
    const theme = useTheme();
    return (
        <Box
            component="img"
            src={theme.palette.mode === 'dark' ? MascoteEscuro : Mascote}
            alt=""
            aria-hidden="true"
            sx={{ width: tamanho, height: tamanho, flexShrink: 0, display: 'block' }}
        />
    );
}

export function OrientacaoDialog({ sugestao, onClose }) {
    const navigate = useNavigate();
    const { tenant } = useParams();
    const rota = rotaDaAcao(tenant, sugestao?.acao);
    return (
        <Dialog open={Boolean(sugestao)} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle>{sugestao?.titulo}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>{sugestao?.texto}</Typography>
                <Typography variant="body2" color="text.secondary">{sugestao?.orientacao}</Typography>
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2 }}>
                    {sugestao?.redigidaPorIA
                        ? 'Sugestão escolhida por regras e redigida por IA a partir dos dados registrados. Não substitui a avaliação técnica.'
                        : 'Sugestão gerada por regras a partir dos dados registrados. Não substitui a avaliação técnica.'}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose}>Fechar</Button>
                {rota && (
                    <Button variant="contained" disableElevation onClick={() => navigate(rota)} sx={{ borderRadius: 2 }}>
                        {sugestao.acao.rotulo}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

/** Card grande do assistente: a sugestão mais importante agora. */
export function AssistenteCard({ sugestao, abertas, carregando }) {
    const navigate = useNavigate();
    const { tenant } = useParams();
    const [orientacao, setOrientacao] = useState(null);
    const rota = rotaDaAcao(tenant, sugestao?.acao);
    const Icone = Lucide[iconeDaAcao(sugestao?.acao)] || Lucide.ArrowRight;

    return (
        <Paper
            variant="outlined"
            sx={{
                height: '100%', borderRadius: 3, display: 'flex', flexDirection: 'column',
                borderColor: alpha(AZUL_CLARO, 0.25),
                background: (t) => `linear-gradient(160deg, ${alpha(AZUL_CLARO, t.palette.mode === 'dark' ? 0.12 : 0.06)}, ${t.palette.background.paper} 70%)`,
            }}
        >
            <Box sx={{ p: { xs: 2.5, sm: 3 }, flex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
                    <AvatarAssistente />
                    <Box>
                        <Typography variant="overline" sx={{ color: AZUL_CLARO, fontWeight: 700, letterSpacing: 1.5, lineHeight: 1.6 }}>
                            Assistente BioAILab
                        </Typography>
                        <Typography fontWeight={700}>Um próximo passo para a qualidade</Typography>
                    </Box>
                </Stack>

                {carregando ? (
                    <Box aria-busy="true" aria-label="Carregando a sugestão do assistente">
                        <Skeleton width="65%" height={32} />
                        <Skeleton width="100%" />
                        <Skeleton width="95%" />
                        <Skeleton width="60%" />
                        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                            <Skeleton variant="rounded" width={190} height={40} sx={{ borderRadius: 2 }} />
                            <Skeleton variant="rounded" width={170} height={40} sx={{ borderRadius: 2 }} />
                        </Stack>
                    </Box>
                ) : sugestao ? (
                    <>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{sugestao.titulo}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{sugestao.texto}</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mt: 2.5 }}>
                            {rota && (
                                <Button
                                    variant="contained"
                                    disableElevation
                                    startIcon={<Icone size={16} />}
                                    onClick={() => navigate(rota)}
                                    sx={{ borderRadius: 2, fontWeight: 700, bgcolor: AZUL_CLARO, '&:hover': { bgcolor: alpha(AZUL_CLARO, 0.85) } }}
                                >
                                    {sugestao.acao.rotulo}
                                </Button>
                            )}
                            <Button onClick={() => setOrientacao(sugestao)} sx={{ fontWeight: 700, color: AZUL_CLARO }}>
                                Ver orientação completa
                            </Button>
                        </Stack>
                    </>
                ) : (
                    <>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Tudo em dia</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Nenhuma pendência nos dados recentes da propriedade. Continue registrando as coletas para o
                            assistente acompanhar a evolução.
                        </Typography>
                    </>
                )}
            </Box>
            <Divider />
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={{ xs: 0.75, sm: 1 }}
                sx={{ px: { xs: 2.5, sm: 3 }, py: 1.75 }}
            >
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => navigate(`/${tenant}/acoes`)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.primary', fontSize: '0.875rem' }}
                >
                    <Lucide.ClipboardList size={16} />
                    {abertas == null ? 'Plano de ações' : abertas === 1 ? '1 ação em aberto' : `${abertas} ações em aberto`}
                </Link>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: { sm: 'right' } }}>
                    {sugestao?.redigidaPorIA ? 'Sugestões por regras · texto redigido por IA' : 'Sugestões por regras · IA não conectada'}
                </Typography>
            </Stack>
            <OrientacaoDialog sugestao={orientacao} onClose={() => setOrientacao(null)} />
        </Paper>
    );
}

/** Faixa do assistente dentro do card da última análise: a sugestão ligada a ela. */
export function AssistenteFaixa({ sugestao }) {
    const [orientacao, setOrientacao] = useState(null);
    return (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: alpha(AZUL_CLARO, 0.05), display: 'flex', gap: 1.5 }}>
            <AvatarAssistente tamanho={48} />
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: AZUL_CLARO }}>Assistente BioAILab</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.7 }}>
                    {sugestao ? sugestao.texto : 'Sem pendências para esta análise.'}
                </Typography>
                {sugestao && (
                    <Link component="button" type="button" underline="hover" onClick={() => setOrientacao(sugestao)}
                        sx={{ mt: 1.25, fontWeight: 700, fontSize: '0.875rem', color: AZUL_CLARO }}>
                        Ver orientação
                    </Link>
                )}
            </Box>
            <OrientacaoDialog sugestao={orientacao} onClose={() => setOrientacao(null)} />
        </Box>
    );
}
